import { range, type TimeRange } from '../time/ranges';
import { toDb, type AudioEnvelope } from './envelope';

export type SilencePresetId = 'gentle' | 'balanced' | 'aggressive';

export interface SilenceParams {
  /**
   * Where the threshold sits between the noise floor (0) and the speech level (1).
   * Adaptive thresholds keep working with constant background noise such as an engine
   * or wind, where a fixed dB threshold would never trigger.
   */
  readonly thresholdRatio: number;
  /** Silences shorter than this are kept (natural pauses). */
  readonly minSilenceSec: number;
  /** Breathing room kept around speech so words are never clipped. */
  readonly paddingSec: number;
  /** Loud blips shorter than this inside a silence (clicks, bumps) are ignored. */
  readonly minSoundSec: number;
}

export const SILENCE_PRESETS: Readonly<Record<SilencePresetId, SilenceParams>> = {
  gentle: { thresholdRatio: 0.22, minSilenceSec: 0.9, paddingSec: 0.25, minSoundSec: 0.12 },
  balanced: { thresholdRatio: 0.32, minSilenceSec: 0.55, paddingSec: 0.15, minSoundSec: 0.1 },
  aggressive: { thresholdRatio: 0.42, minSilenceSec: 0.3, paddingSec: 0.08, minSoundSec: 0.08 },
};

export interface LevelStats {
  readonly noiseFloorDb: number;
  readonly speechDb: number;
  readonly thresholdDb: number;
}

/** Minimum gap between noise floor and speech for the audio to have meaningful silences. */
const MIN_DYNAMIC_RANGE_DB = 8;
/** Anything below this is treated as silence regardless of the adaptive threshold. */
const ABSOLUTE_SILENCE_DB = -60;

function percentile(sorted: Float32Array, p: number): number {
  if (sorted.length === 0) return 0;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.round(p * (sorted.length - 1))));
  return sorted[index]!;
}

export function measureLevels(envelope: AudioEnvelope, thresholdRatio: number): LevelStats | null {
  if (envelope.rms.length === 0) return null;
  const db = Float32Array.from(envelope.rms, toDb).sort();
  const noiseFloorDb = percentile(db, 0.1);
  const speechDb = percentile(db, 0.9);
  if (speechDb - noiseFloorDb < MIN_DYNAMIC_RANGE_DB) return null;
  const thresholdDb = noiseFloorDb + (speechDb - noiseFloorDb) * thresholdRatio;
  return { noiseFloorDb, speechDb, thresholdDb };
}

/** Converts runs of `value` in `flags` shorter than `minWindows` into `!value`. */
function fillShortRuns(flags: Uint8Array, value: 0 | 1, minWindows: number): void {
  let i = 0;
  while (i < flags.length) {
    if (flags[i] !== value) {
      i++;
      continue;
    }
    let j = i;
    while (j < flags.length && flags[j] === value) j++;
    const bounded = i > 0 && j < flags.length;
    if (bounded && j - i < minWindows) flags.fill(value === 1 ? 0 : 1, i, j);
    i = j;
  }
}

export interface SilenceDetection {
  /** Ranges that will be removed (already shrunk by padding). */
  readonly silences: TimeRange[];
  readonly levels: LevelStats | null;
}

/**
 * Finds removable silences in an envelope. Returns no silences when the audio has
 * no usable dynamic range (e.g. constant music or no audio at all) — it is always
 * safer to cut nothing than to cut speech.
 */
export function detectSilences(envelope: AudioEnvelope, params: SilenceParams, durationSec: number): SilenceDetection {
  const levels = measureLevels(envelope, params.thresholdRatio);
  if (!levels) return { silences: [], levels: null };

  const { rms, windowSec } = envelope;
  const silent = new Uint8Array(rms.length);
  for (let i = 0; i < rms.length; i++) {
    const db = toDb(rms[i]!);
    silent[i] = db < levels.thresholdDb || db < ABSOLUTE_SILENCE_DB ? 1 : 0;
  }
  fillShortRuns(silent, 0, Math.round(params.minSoundSec / windowSec));

  const end = Math.max(durationSec, rms.length * windowSec);
  const silences: TimeRange[] = [];
  let i = 0;
  while (i < silent.length) {
    if (!silent[i]) {
      i++;
      continue;
    }
    let j = i;
    while (j < silent.length && silent[j]) j++;
    const runStart = i * windowSec;
    const runEnd = j === silent.length ? end : j * windowSec;
    // No padding is needed on the file's outer edges: there is no speech to protect there.
    const cutStart = i === 0 ? 0 : runStart + params.paddingSec;
    const cutEnd = j === silent.length ? end : runEnd - params.paddingSec;
    if (runEnd - runStart >= params.minSilenceSec && cutEnd > cutStart) silences.push(range(cutStart, cutEnd));
    i = j;
  }
  return { silences, levels };
}
